import type { PixelEvent } from '@/events';

import type {
  DestinationAdapter,
  DestinationContext,
  DestinationHooks,
  DestinationResult,
} from './types';
import type { DestinationErrorHandler } from './DestinationErrorHandler';
import type { DestinationLogger } from './DestinationLogger';

/**
 * Executes a single destination with timeout (AbortSignal), hooks and error isolation.
 */
export class DestinationExecutor {
  private readonly logger: DestinationLogger;

  private readonly errorHandler: DestinationErrorHandler;

  private readonly hooks: DestinationHooks;

  private readonly timeoutMs: number;

  private readonly debug: boolean;

  constructor(options: {
    logger: DestinationLogger;
    errorHandler: DestinationErrorHandler;
    hooks?: DestinationHooks;
    timeoutMs: number;
    debug?: boolean;
  }) {
    this.logger = options.logger;
    this.errorHandler = options.errorHandler;
    this.hooks = options.hooks ?? {};
    this.timeoutMs = options.timeoutMs;
    this.debug = options.debug ?? false;
  }

  async execute(
    adapter: DestinationAdapter,
    event: PixelEvent
  ): Promise<DestinationResult> {
    const startedAt = Date.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

    const context: DestinationContext = {
      event,
      signal: controller.signal,
      timeoutMs: this.timeoutMs,
      debug: this.debug,
    };

    try {
      await this.hooks.beforeExecute?.(adapter, event);

      const result = await adapter.execute(context);
      const enriched: DestinationResult = {
        ...result,
        adapter: result.adapter || adapter.name,
        duration: result.duration || Date.now() - startedAt,
        timestamp: result.timestamp || Date.now(),
      };

      await this.hooks.afterExecute?.(adapter, event, enriched);

      if (enriched.status === 'success') {
        await this.hooks.onSuccess?.(adapter, event, enriched);
      } else if (enriched.status === 'failed' || enriched.status === 'retry') {
        await this.hooks.onFailure?.(adapter, event, enriched);
      } else if (
        enriched.status === 'ignored' ||
        enriched.status === 'skipped'
      ) {
        await this.hooks.onIgnored?.(adapter, event, enriched);
      }

      if (this.debug) {
        this.logger
          .forAdapter(adapter.name)
          .debug(`Result ${enriched.status} in ${enriched.duration}ms`);
      }

      return enriched;
    } catch (error) {
      const failed = this.errorHandler.toFailedResult(
        adapter,
        error,
        Date.now() - startedAt
      );
      await this.hooks.onFailure?.(adapter, event, failed);
      await this.hooks.afterExecute?.(adapter, event, failed);
      return failed;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
